import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { businessInfo } from "../data/content";

const navItems = [
  { label: "Accueil", path: "/" },
  {
    label: "Services",
    path: "/services",
    children: [
      { label: "Dépannage plomberie", path: "/services/depannage-plomberie" },
      { label: "Installation sanitaire", path: "/services/installation-sanitaire" },
      { label: "Chauffage", path: "/services/chauffage" },
      { label: "Recherche de fuites", path: "/services/recherche-de-fuites" },
      { label: "Rénovation salle de bain", path: "/services/renovation-salle-de-bain" },
      { label: "Urgence plomberie", path: "/services/urgence-plomberie" },
      { label: "Entretien chaudière", path: "/services/entretien-chaudiere" },
    ],
  },
  { label: "Zones", path: "/zones-intervention" },
  { label: "Avis", path: "/avis-clients" },
  { label: "FAQ", path: "/faq" },
  { label: "Contact", path: "/contact" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    // z-[60] : le header doit rester AU-DESSUS du bandeau cookies (z-50), sinon
    // le menu mobile deroulant passe dessous et le bandeau intercepte les clics
    // sur les derniers liens (Contact, Devis) sur les ecrans courts.
    <header className="bg-primary text-white sticky top-0 z-[60] shadow-lg" data-testid="header">
      {/* Top bar */}
      <div className="bg-secondary py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm font-medium">
          <span className="hidden sm:block tracking-wide uppercase font-accent">Urgence 7j/7 - Devis gratuit</span>
          <a href={businessInfo.phoneTel} className="flex items-center gap-2 hover:opacity-90 transition-opacity font-bold" data-testid="header-phone-link">
            <Phone size={16} />
            {businessInfo.phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-xl font-bold tracking-tight uppercase" data-testid="header-logo">
            Pereira<span className="text-secondary"> Plomberie</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    className={`px-3 py-2 text-sm font-medium uppercase tracking-wider flex items-center gap-1 transition-colors ${
                      isActive(item.path) ? "text-secondary" : "hover:text-secondary"
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label} <ChevronDown size={14} />
                  </Link>
                  <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-50">
                    <div className="bg-white text-primary shadow-xl rounded-sm border border-slate-200 min-w-[260px]">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="block px-4 py-3 text-sm hover:bg-muted hover:text-secondary transition-colors border-b border-slate-100 last:border-0"
                          data-testid={`nav-sub-${child.path.split("/").pop()}`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                    isActive(item.path) ? "text-secondary" : "hover:text-secondary"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              )
            )}
            <Link to="/contact" className="ml-3 bg-secondary hover:bg-secondary/90 text-white font-bold uppercase tracking-wider py-2 px-5 rounded-sm text-sm transition-all active:scale-95" data-testid="nav-cta-devis">
              Devis gratuit
            </Link>
          </div>

          {/* Mobile toggle */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2" data-testid="mobile-menu-toggle" aria-label="Menu">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 border-t border-white/10" data-testid="mobile-menu">
            {navItems.map((item) => (
              <div key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => !item.children && setMobileOpen(false)}
                  className={`block py-3 text-sm font-medium uppercase tracking-wider ${isActive(item.path) ? "text-secondary" : ""}`}
                >
                  {item.children ? (
                    <button onClick={(e) => { e.preventDefault(); setServicesOpen(!servicesOpen); }} className="flex items-center gap-1 w-full">
                      {item.label} <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                    </button>
                  ) : (
                    item.label
                  )}
                </Link>
                {item.children && servicesOpen && (
                  <div className="pl-4 border-l-2 border-secondary ml-2">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={() => setMobileOpen(false)}
                        className="block py-2 text-sm opacity-80 hover:opacity-100 hover:text-secondary"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="block mt-3 bg-secondary text-center text-white font-bold uppercase tracking-wider py-3 rounded-sm text-sm">
              Devis gratuit
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
}
